export const WALLET_IMAGE_SIZES = [
  { name: 'Icon 29x29', width: 29, height: 29, category: 'Apple Wallet - Icons', imageType: 'icon' },
  { name: 'Icon 58x58', width: 58, height: 58, category: 'Apple Wallet - Icons', imageType: 'icon' },
  { name: 'Icon 87x87', width: 87, height: 87, category: 'Apple Wallet - Icons', imageType: 'icon' },
  { name: 'Strip 375x98', width: 375, height: 98, category: 'Apple Wallet - Strips', imageType: 'strip' },
  { name: 'Strip 750x196', width: 750, height: 196, category: 'Apple Wallet - Strips', imageType: 'strip' },
  { name: 'Strip 1125x294', width: 1125, height: 294, category: 'Apple Wallet - Strips', imageType: 'strip' },
  { name: 'Logo 840x840', width: 840, height: 840, category: 'Google Wallet', imageType: 'icon' },
  { name: 'Hero Image 1032x336', width: 1032, height: 336, category: 'Google Wallet', imageType: 'strip' },
  { name: 'Logo 160x50', width: 160, height: 50, category: 'Apple Wallet - Logos', imageType: 'logo', isRarelyUsed: true },
  { name: 'Logo 320x100', width: 320, height: 100, category: 'Apple Wallet - Logos', imageType: 'logo', isRarelyUsed: true },
  { name: 'Logo 480x150', width: 480, height: 150, category: 'Apple Wallet - Logos', imageType: 'logo', isRarelyUsed: true },
  { name: 'Background 180x220', width: 180, height: 220, category: 'Apple Wallet - Background', imageType: 'strip', isRarelyUsed: true },
  { name: 'Background 360x440', width: 360, height: 440, category: 'Apple Wallet - Background', imageType: 'strip', isRarelyUsed: true },
  { name: 'Thumbnail 90x90', width: 90, height: 90, category: 'Apple Wallet - Thumbnail', imageType: 'icon', isRarelyUsed: true },
  { name: 'Thumbnail 180x180', width: 180, height: 180, category: 'Apple Wallet - Thumbnail', imageType: 'icon', isRarelyUsed: true },
  { name: 'Thumbnail 270x270', width: 270, height: 270, category: 'Apple Wallet - Thumbnail', imageType: 'icon', isRarelyUsed: true },
];

export const DEFAULT_PADDING = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  color: 'transparent',
};

const resolveFillColor = fill => {
  if (!fill || fill === 'transparent') return null;
  if (fill === 'white') return '#ffffff';
  if (fill === 'black') return '#000000';
  return fill;
};

export const groupWalletImageSizes = (includeRarelyUsed = false) => {
  const grouped = new Map();

  WALLET_IMAGE_SIZES.filter(size => includeRarelyUsed || !size.isRarelyUsed).forEach(
    size => {
      if (!grouped.has(size.category)) {
        grouped.set(size.category, []);
      }
      grouped.get(size.category).push(size);
    }
  );

  return Array.from(grouped.entries()).map(([category, sizes]) => ({
    category,
    sizes,
  }));
};

const loadFileImage = file =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to load image: ${file?.name || 'unknown file'}`));
    };

    image.src = objectUrl;
  });

const quantizeChannel = value => Math.max(0, Math.min(255, Math.round(value / 32) * 32));

const rgbToHex = (r, g, b) =>
  `#${[r, g, b]
    .map(channel => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, '0'))
    .join('')}`;

export const extractImagePalette = async (file, maxColors = 8) => {
  const image = await loadFileImage(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  if (!ctx) {
    throw new Error('Canvas rendering is not available in this browser.');
  }

  const sampleWidth = 32;
  const scale = sampleWidth / Math.max(image.width, 1);
  const sampleHeight = Math.max(1, Math.round(image.height * scale));

  canvas.width = sampleWidth;
  canvas.height = sampleHeight;
  ctx.drawImage(image, 0, 0, sampleWidth, sampleHeight);

  const { data } = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
  const colorCounts = new Map();

  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3];
    if (alpha < 180) continue;

    const red = quantizeChannel(data[index]);
    const green = quantizeChannel(data[index + 1]);
    const blue = quantizeChannel(data[index + 2]);
    const hex = rgbToHex(red, green, blue);

    colorCounts.set(hex, (colorCounts.get(hex) || 0) + 1);
  }

  return Array.from(colorCounts.entries())
    .sort((left, right) => right[1] - left[1])
    .slice(0, maxColors)
    .map(([hex]) => hex);
};

export const resizeImageFile = async ({
  file,
  width,
  height,
  backgroundFill = 'transparent',
  padding = DEFAULT_PADDING,
}) => {
  const image = await loadFileImage(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas rendering is not available in this browser.');
  }

  const normalizedPadding = {
    top: Number(padding?.top || 0),
    right: Number(padding?.right || 0),
    bottom: Number(padding?.bottom || 0),
    left: Number(padding?.left || 0),
    color:
      padding?.color === 'transparent' ||
      padding?.color === 'white' ||
      padding?.color === 'black'
        ? padding.color
        : padding?.color || 'transparent',
  };

  const finalWidth = width + normalizedPadding.left + normalizedPadding.right;
  const finalHeight = height + normalizedPadding.top + normalizedPadding.bottom;

  canvas.width = finalWidth;
  canvas.height = finalHeight;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.clearRect(0, 0, finalWidth, finalHeight);

  const paddingFill = resolveFillColor(normalizedPadding.color);
  const baseFill = resolveFillColor(backgroundFill);

  if (paddingFill) {
    ctx.fillStyle = paddingFill;
    ctx.fillRect(0, 0, finalWidth, finalHeight);
  } else if (baseFill) {
    ctx.fillStyle = baseFill;
    ctx.fillRect(0, 0, finalWidth, finalHeight);
  }

  const scale = Math.min(width / image.width, height / image.height);
  const scaledWidth = image.width * scale;
  const scaledHeight = image.height * scale;
  const x = normalizedPadding.left + (width - scaledWidth) / 2;
  const y = normalizedPadding.top + (height - scaledHeight) / 2;

  ctx.drawImage(image, x, y, scaledWidth, scaledHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Failed to generate a PNG image.'));
          return;
        }
        resolve(blob);
      },
      'image/png',
      1
    );
  });
};

const applyFilePattern = ({ baseName, config, pattern }) =>
  (pattern || '{basename}_{size}')
    .replace(/\{basename\}/g, baseName)
    .replace(/\{size\}/g, config.name.replace(/\s+/g, '_'))
    .replace(/\{width\}/g, String(config.width))
    .replace(/\{height\}/g, String(config.height));

export const downloadResizedImages = async ({
  images,
  baseName = 'wallet-image',
  pattern = '{basename}_{size}',
}) => {
  for (const { blob, config } of images) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${applyFilePattern({ baseName, config, pattern })}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    await new Promise(resolve => window.setTimeout(resolve, 100));
  }
};
